import Select from "react-select";
import styled from "styled-components";
import { ChevronDownIcon } from "ui/icons/inputIcons/ChevronDownIcon";
import { themeColor, themeFontSize } from "ui/theme";
import { HelperText } from "../helperText/HelperText";
import { customStyles } from "./selectStyle";

export const SelectButton = ({
  options,
  value,
  onChange,
  placeholder,
  label,
  labelSize,
  errorText,
  isError,
  icon,
  bgColor,
  valueColor,
  placeholderColor,
  borderColor,
  chevronColor,
  textAlign,
  showDelete,
  showIndicator = true,
  isDisabled = false,
  height,
  width,
  rounded,
  menuPlacement = "auto",
  cursor,
}) => {
  const resolvedCursor = cursor || (isDisabled ? "default" : "pointer");

  return (
    <SelectContainer tabIndex={0} width={width} cursor={resolvedCursor}>
      {label ? <HelperText>{label}</HelperText> : null}
      <InputContainer
        isError={isError}
        bgColor={bgColor}
        size={labelSize}
        valueColor={valueColor}
        placeholderColor={placeholderColor}
        borderColor={borderColor}
        hasIcon={!!icon}
        showIndicator={showIndicator}
        height={height}
        textAlign={textAlign}
        rounded={rounded}
        cursor={resolvedCursor}
      >
        {icon && (
          <InputIcon isError={isError} hasValue={!!value} position="left">
            {icon}
          </InputIcon>
        )}
        <Select
          value={value}
          onChange={onChange}
          isDisabled={isDisabled}
          options={
            showDelete
              ? [...options, { value: "delete", label: "Delete" }]
              : options
          }
          placeholder={placeholder}
          className="auto-complete-container"
          classNamePrefix="auto-complete"
          isSearchable={false}
          menuPlacement={menuPlacement}
          styles={{
            ...customStyles,
            control: () => null,
            SelectContainer: () => null,
            NoOptionsMessage: () => null,
          }}
          components={{
            IndicatorsContainer: () => null,
          }}
        />
        {showIndicator ? (
          <InputIcon isError={isError}>
            <ChevronDownIcon color={chevronColor} width={10} height={10} strokeWidth={3} />
          </InputIcon>
        ) : null}
      </InputContainer>

      {isError ? (
        <HelperText color="pink" size="small">
          {errorText}
        </HelperText>
      ) : null}
    </SelectContainer>
  );
};

const SelectContainer = styled.div`
  width: ${({ width = "100%" }) => width};
  display: grid;
  gap: 1rem;
  cursor: ${({ cursor = "pointer" }) => cursor};
`;

export const InputContainer = styled.label`
  position: relative;
  cursor: ${({ cursor = "pointer" }) => cursor};
  display: flex;
  align-items: center;
  gap: 1rem;
  height: ${({ height = "64px" }) => height};
  font-size: var(--auto-complete-font-size, var(--select-button-font-size, ${themeFontSize("regular")}));
  border-width: 2px;
  border-style: solid;
  border-color: ${({ isError, focus, borderColor = "lightBlue" }) =>
    isError
      ? themeColor("pink")
      : focus
        ? themeColor("blue")
        : themeColor(borderColor)};
  border-radius: ${({ rounded }) => (rounded ? "99px" : "8px")};
  background-color: ${({ bgColor = "white" }) => themeColor(bgColor)};

  & .auto-complete-container,
  .auto-complete__control {
    display: flex;
    align-items: center;
  }
  & .auto-complete-container,
  .auto-complete__control,
  .auto-complete__menu-notice {
    width: 100%;
    height: 100%;
    padding: 0;
    margin: 0;
    border-radius: 8px;
    cursor: ${({ cursor = "pointer" }) => cursor};
  }

  & .auto-complete__value-container {
    text-align: ${({ textAlign = "left" }) => textAlign};
    padding: 0; 
  }

  & .auto-complete__menu,
  & .auto-complete__option {
    text-align: left;
  }

  .auto-complete__menu-notice {
    width: 100%;
    height: 100%;
    padding: 0.8rem;
  }

  & .auto-complete__placeholder {
    color: ${({ placeholderColor = "placeholder" }) =>
      themeColor(placeholderColor)};
    font-size: inherit;
    padding-left: ${({ textAlign }) => (textAlign === "center" ? "0" : "1rem")};
    white-space: nowrap;
  }

  & .auto-complete__single-value {
    font-size: inherit;
    color: ${({ valueColor = "txt" }) => themeColor(valueColor)};
    padding-left: ${({ textAlign }) => (textAlign === "center" ? "0" : "1rem")};
    ${({ textAlign }) => textAlign === "right" && `
      text-align: right;
      padding-left: 0;
      padding-right: 2.3rem;
    `}
  }

  .auto-complete__input-container {
    color: ${themeColor("txt")};

    .auto-complete__input {
      width: 100%;
      height: 100%;
      font-size: var(--auto-complete-font-size, ${themeFontSize("regular")}) !important;
    }
  }
`;

const InputIcon = styled.label`
  height: 100%;
  width: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${({ isError, hasValue }) =>
    isError
      ? themeColor("pink")
      : hasValue
        ? themeColor("blue")
        : themeColor("border")};
  position: absolute;
  pointer-events: none;
  ${({ position }) => (position === "left" ? "left:0.5rem" : "right:0.5rem")};
`;
